import type { PublicInvoice } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Field, Loading, cx, formatCents, formatDate } from "../components/ui";
import { RichText } from "../components/rich-text";

// The few Stripe.js calls this page uses (loaded from js.stripe.com, as Stripe requires).
interface StripeElement {
  mount(el: HTMLElement): void;
  on(event: "change", cb: (e: { complete: boolean; error?: { message: string } }) => void): void;
  destroy(): void;
}
interface StripeJs {
  elements(): { create(type: "card", opts?: object): StripeElement };
  createPaymentMethod(p: { type: "card"; card: StripeElement; billing_details: { name: string } }): Promise<{ paymentMethod?: { id: string }; error?: { message: string } }>;
  handleNextAction(p: { clientSecret: string }): Promise<{ paymentIntent?: { id: string; status: string }; error?: { message: string } }>;
  collectBankAccountForPaymentIntent(p: { clientSecret: string; params: { payment_method_type: "us_bank_account"; payment_method_data: { billing_details: { name: string; email: string } } } }): Promise<{ paymentIntent?: { id: string; status: string }; error?: { message: string } }>;
  confirmUsBankAccountPayment(clientSecret: string): Promise<{ paymentIntent?: { id: string; status: string }; error?: { message: string } }>;
}
declare global {
  interface Window {
    Stripe?: (key: string) => StripeJs;
  }
}

let stripeLoad: Promise<void> | null = null;
function loadStripe(key: string): Promise<StripeJs> {
  stripeLoad ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Couldn't load the payment form. Check your connection and reload."));
    document.head.appendChild(s);
  });
  return stripeLoad.then(() => window.Stripe!(key));
}

/** The customer's invoice and pay page: no account, the link is the key. */
export { loadStripe };

export function PublicPayPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["public-invoice", token], queryFn: () => get<PublicInvoice>(`/api/public/invoices/${token}`), retry: false, refetchInterval: (x) => (x.state.data?.pending.length ? 10_000 : false) });
  const set = (d: PublicInvoice) => qc.setQueryData(["public-invoice", token], d);
  useEffect(() => {
    if (q.data?.company.primaryColor) document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
  }, [q.data?.company.primaryColor]);

  if (q.isLoading) return <div className="grid min-h-dvh place-items-center"><Loading /></div>;
  if (q.error || !q.data) return <div className="mx-auto max-w-lg px-4 py-16"><Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert></div>;
  const inv = q.data;
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-[var(--color-sidebar)] text-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-center gap-3">
            {inv.company.logoUrl && <img src={inv.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
            <span className="text-lg font-semibold">{inv.company.name}</span>
          </div>
          <a href={`tel:${inv.company.phone}`} className="text-sm text-white/80 hover:text-white">{inv.company.phone}</a>
        </div>
      </header>
      <main className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Invoice {inv.number}{inv.title && ` · ${inv.title}`}</h1>
          <p className="text-ink-soft">For {inv.customerName}{inv.address && ` · ${inv.address}`}</p>
        </div>
        <section className="rounded-[var(--radius-panel)] bg-white px-5 py-4 text-sm shadow-[var(--shadow-card)]">
          <table className="w-full">
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i} className="border-b border-mist">
                  <td className="py-2 whitespace-pre-wrap">
                    {l.description}
                    {l.quantity !== undefined && l.unitPriceCents !== undefined && <div className="text-xs text-muted">{l.quantity.toLocaleString("en-US", { maximumFractionDigits: 4 })} × {formatCents(l.unitPriceCents)}</div>}
                  </td>
                  <td className="py-2 text-right">{formatCents(l.amountCents)}</td>
                </tr>
              ))}
              {inv.paidCents > 0 && (
                <tr>
                  <td className="py-1.5 text-muted">Paid</td>
                  <td className="py-1.5 text-right text-muted">−{formatCents(inv.paidCents)}</td>
                </tr>
              )}
              <tr className="text-base font-semibold">
                <td className="py-2">Amount due</td>
                <td className="py-2 text-right">{formatCents(inv.balanceCents)}</td>
              </tr>
            </tbody>
          </table>
          {inv.dueDate && <p className="mt-1 text-muted">Due {formatDate(inv.dueDate)}</p>}
          {inv.memo && <RichText className="mt-2 text-ink-soft" text={inv.memo} />}
          <a href={`/api/public/invoices/${token}/pdf`} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">
            Download PDF
          </a>
        </section>
        {inv.status === "paid" ? (
          <Alert tone="ok">Paid in full. Thank you!</Alert>
        ) : inv.pending.length ? (
          <Alert tone="info">Your bank payment of {formatCents(inv.pending[0]!.amountCents)} is processing. Bank payments take a few business days; this page updates when it's done.</Alert>
        ) : !inv.stripeKey ? (
          <Alert tone="info">To pay, call us at {inv.company.phone}.</Alert>
        ) : (
          <Pay base={`/api/public/invoices/${token}`} resultKey="invoice" what="Invoice" amountCents={inv.balanceCents} customerName={inv.customerName} cardAllowed={inv.cardAllowed} cardLimitCents={inv.cardLimitCents} surchargeBps={inv.surchargeBps} stripeKey={inv.stripeKey!} onDone={set} />
        )}
        <p className="pb-6 text-center text-xs text-muted">
          {inv.company.name}
          {inv.company.email && ` · ${inv.company.email}`} · Payments are processed securely by Stripe.
        </p>
      </main>
    </div>
  );
}

/** How the customer pays: bank (ACH) or card, at `base` (an invoice's or a statement's public routes). */
export interface PayProps<T> {
  base: string;
  /** Where the updated page comes back in a card payment's answer. */
  resultKey: "invoice" | "statement";
  /** What's being paid, on the card quote ("Invoice", "Statement"). */
  what: string;
  amountCents: number;
  customerName: string;
  cardAllowed: boolean;
  cardLimitCents: number;
  surchargeBps: number;
  stripeKey: string;
  onDone: (d: T) => void;
}

export function Pay<T>(inv: PayProps<T>) {
  const { onDone } = inv;
  const [method, setMethod] = useState<"ach" | "card">("ach");
  const [name, setName] = useState(inv.customerName);
  const [email, setEmail] = useState("");
  const stripe = useQuery({ queryKey: ["stripe", inv.stripeKey], queryFn: () => loadStripe(inv.stripeKey), staleTime: Infinity });
  const pct = (inv.surchargeBps / 100).toFixed(inv.surchargeBps % 100 ? 2 : 0);

  return (
    <section className="flex flex-col gap-4 rounded-[var(--radius-panel)] bg-white px-5 py-5 shadow-[var(--shadow-card)]">
      <h2 className="text-lg font-semibold">Pay {formatCents(inv.amountCents)}</h2>
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="How to pay">
        {(
          [
            ["ach", "Bank account (ACH)", "Free. Takes a few business days."],
            ["card", "Credit or debit card", inv.cardAllowed ? `A ${pct}% fee applies to credit cards (not debit).` : `Cards are for payments up to ${formatCents(inv.cardLimitCents)}.`],
          ] as const
        ).map(([key, label, note]) => (
          <button
            key={key}
            role="radio"
            aria-checked={method === key}
            disabled={key === "card" && !inv.cardAllowed}
            onClick={() => setMethod(key)}
            className={cx("rounded-[var(--radius-control)] border px-4 py-3 text-left disabled:opacity-50", method === key ? "border-brand ring-2 ring-brand/20" : "border-line hover:border-ink-soft")}
          >
            <div className="font-semibold">{label}</div>
            <div className="text-sm text-muted">{note}</div>
          </button>
        ))}
      </div>
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      {stripe.error && <Alert>{errorMessage(stripe.error)}</Alert>}
      {!stripe.data ? <Loading /> : method === "ach" ? <Ach stripe={stripe.data} base={inv.base} name={name} email={email} setEmail={setEmail} onDone={onDone} /> : <Card stripe={stripe.data} base={inv.base} resultKey={inv.resultKey} what={inv.what} name={name} onDone={onDone} />}
    </section>
  );
}

function Ach<T>({ stripe, base, name, email, setEmail, onDone }: { stripe: StripeJs; base: string; name: string; email: string; setEmail: (v: string) => void; onDone: (d: T) => void }) {
  const pay = useMutation({
    mutationFn: async () => {
      const { clientSecret } = await post<{ clientSecret: string }>(`${base}/ach`);
      const linked = await stripe.collectBankAccountForPaymentIntent({ clientSecret, params: { payment_method_type: "us_bank_account", payment_method_data: { billing_details: { name, email } } } });
      if (linked.error) throw new Error(linked.error.message);
      if (linked.paymentIntent?.status === "requires_payment_method") throw new Error("No bank account was linked.");
      const confirmed = await stripe.confirmUsBankAccountPayment(clientSecret);
      if (confirmed.error) throw new Error(confirmed.error.message);
      return post<T>(`${base}/confirmed`, { paymentIntentId: confirmed.paymentIntent!.id });
    },
    onSuccess: onDone,
  });
  return (
    <div className="flex flex-col gap-3">
      <Field label="Email (for the payment receipt)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      {pay.error && <Alert>{errorMessage(pay.error)}</Alert>}
      <p className="text-xs text-muted">You'll log in to your bank through Stripe to pick the account. By paying, you authorize {"NMT Construction"} to debit that account for this amount.</p>
      <div>
        <Button variant="primary" busy={pay.isPending} disabled={name.trim().length < 2 || !/\S+@\S+\.\S+/.test(email)} onClick={() => pay.mutate()}>
          Link bank and pay
        </Button>
      </div>
    </div>
  );
}

function Card<T>({ stripe, base, resultKey, what, name, onDone }: { stripe: StripeJs; base: string; resultKey: "invoice" | "statement"; what: string; name: string; onDone: (d: T) => void }) {
  const mount = useRef<HTMLDivElement>(null);
  const element = useRef<StripeElement | null>(null);
  const [complete, setComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [quote, setQuote] = useState<{ pm: string; brand: string; last4: string; funding: string; amountCents: number; surchargeCents: number; totalCents: number } | null>(null);
  const [consent, setConsent] = useState(false);
  useEffect(() => {
    const el = stripe.elements().create("card", { hidePostalCode: false, style: { base: { fontSize: "16px" } } });
    el.mount(mount.current!);
    el.on("change", (e) => {
      setComplete(e.complete);
      setCardError(e.error?.message ?? null);
      setQuote(null);
    });
    element.current = el;
    return () => el.destroy();
  }, [stripe]);
  const price = useMutation({
    mutationFn: async () => {
      const r = await stripe.createPaymentMethod({ type: "card", card: element.current!, billing_details: { name } });
      if (r.error) throw new Error(r.error.message);
      const q = await post<{ brand: string; last4: string; funding: string; amountCents: number; surchargeCents: number; totalCents: number }>(`${base}/card-quote`, { paymentMethodId: r.paymentMethod!.id });
      return { pm: r.paymentMethod!.id, ...q };
    },
    onSuccess: setQuote,
  });
  const pay = useMutation({
    mutationFn: async () => {
      const r = await post<{ status: string; clientSecret: string | null } & Record<"invoice" | "statement", T>>(`${base}/card-pay`, { paymentMethodId: quote!.pm, surchargeCents: quote!.surchargeCents, consent: true });
      if (r.clientSecret) {
        // The bank wants to check it's you (3-D Secure).
        const next = await stripe.handleNextAction({ clientSecret: r.clientSecret });
        if (next.error) throw new Error(next.error.message);
        return post<T>(`${base}/confirmed`, { paymentIntentId: next.paymentIntent!.id });
      }
      if (r.status !== "succeeded") throw new Error("Your card was declined. Try another card, or pay by bank.");
      return r[resultKey];
    },
    onSuccess: onDone,
  });
  return (
    <div className="flex flex-col gap-3">
      <div>
        <span className="mb-1 block text-sm font-semibold text-ink-soft">Card</span>
        <div ref={mount} className="rounded-[var(--radius-control)] border border-line bg-white px-3 py-3" />
        {cardError && <p className="mt-1 text-sm text-danger">{cardError}</p>}
      </div>
      {(price.error || pay.error) && <Alert>{errorMessage(price.error ?? pay.error)}</Alert>}
      {!quote ? (
        <div>
          <Button variant="primary" busy={price.isPending} disabled={!complete || name.trim().length < 2} onClick={() => price.mutate()}>
            Continue
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-[var(--radius-control)] bg-mist/60 p-4 text-sm">
          <table className="w-full">
            <tbody>
              <tr>
                <td>{what}</td>
                <td className="text-right">{formatCents(quote.amountCents)}</td>
              </tr>
              <tr>
                <td>{quote.surchargeCents ? "Credit card surcharge" : `No surcharge (${quote.funding} card)`}</td>
                <td className="text-right">{formatCents(quote.surchargeCents)}</td>
              </tr>
              <tr className="font-semibold">
                <td>Charged to {quote.brand} ending {quote.last4}</td>
                <td className="text-right">{formatCents(quote.totalCents)}</td>
              </tr>
            </tbody>
          </table>
          {quote.surchargeCents > 0 && (
            <p>
              A surcharge of {formatCents(quote.surchargeCents)} applies to credit cards. It isn't more than our cost of taking the card. Paying by bank (ACH) or debit card has no surcharge.
            </p>
          )}
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-brand)]" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>I agree to pay {formatCents(quote.totalCents)} with this card.</span>
          </label>
          <div>
            <Button variant="primary" busy={pay.isPending} disabled={!consent} onClick={() => pay.mutate()}>
              Pay {formatCents(quote.totalCents)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
